/**
 * Asset store — content-addressed parsed meshes in IndexedDB.
 * Format v2: [u32 pos][u32 nrm][u32 idx][u32 edge] + raw buffers.
 * (v1 lacked the edge field — detected via blob size and still readable.)
 */

import type { MeshData } from '../geometry/types';
import { db } from './db';

export async function saveParsedMesh(name: string, hash: string, mesh: MeshData): Promise<void> {
  const header = new Uint32Array([
    mesh.positions.length,
    mesh.normals.length,
    mesh.indices.length,
    mesh.edgePositions.length,
  ]);
  // Fresh copies guarantee plain-ArrayBuffer backing (BlobPart requirement).
  const blob = new Blob([
    header,
    mesh.positions.slice(),
    mesh.normals.slice(),
    mesh.indices.slice(),
    mesh.edgePositions.slice(),
  ] as BlobPart[]);
  await db.assets.put({
    hash,
    kind: 'mesh',
    mimeType: 'application/x-novacad-mesh',
    name,
    byteLength: blob.size,
    data: blob,
    createdAt: Date.now(),
  });
}

/** Raw STEP/IGES text, content-addressed — the source for import-step. */
export async function saveBrepText(name: string, hash: string, text: string): Promise<void> {
  await db.assets.put({
    hash,
    kind: 'brep',
    mimeType: 'model/step',
    name,
    byteLength: text.length,
    data: new Blob([text]),
    createdAt: Date.now(),
  });
}

export async function loadBrepText(hash: string): Promise<string | null> {
  const rec = await db.assets.get(hash);
  // Kind guard: a mesh blob under this key would parse as garbage STEP —
  // better a clean "missing from the asset store" than a cryptic kernel error.
  return rec && rec.kind === 'brep' ? rec.data.text() : null;
}

export async function loadParsedMesh(hash: string): Promise<MeshData | null> {
  const rec = await db.assets.get(hash);
  // Only packed-mesh records are readable here (see kind guard rationale above).
  if (!rec || rec.kind !== 'mesh') return null;
  const buf = await rec.data.arrayBuffer();
  if (buf.byteLength < 12) return null;
  const head = new Uint32Array(buf, 0, buf.byteLength >= 16 ? 4 : 3);
  const isV2 =
    head.length === 4 &&
    16 + (head[0]! + head[1]! + head[2]! + head[3]!) * 4 === buf.byteLength;
  const headerBytes = isV2 ? 16 : 12;
  const [posLen, nrmLen, idxLen] = [head[0]!, head[1]!, head[2]!];
  const edgeLen = isV2 ? head[3]! : 0;
  let off = headerBytes;
  const positions = new Float32Array(buf, off, posLen);
  off += posLen * 4;
  const normals = new Float32Array(buf, off, nrmLen);
  off += nrmLen * 4;
  const indices = new Uint32Array(buf, off, idxLen);
  off += idxLen * 4;
  const edgePositions = isV2 ? new Float32Array(buf, off, edgeLen) : new Float32Array(0);
  return { positions, normals, indices, edgePositions };
}
